import React, { useState } from "react";
import {
  Box, Typography, TextField, Button, FormControl, InputLabel, Select,
  MenuItem, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, CircularProgress, Dialog, DialogContent, IconButton,
  Chip, Tooltip,
} from "@mui/material";
import { PhotoCamera, Close, ZoomIn, Download } from "@mui/icons-material";
import { useGetAdminSubmissionsMutation } from "state/api";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";

const BASE = process.env.REACT_APP_BASE_URL || "http://localhost:5000";

/* single-image columns */
const IMAGE_FIELDS = ["rx_photo", "pob_invoice", "prescription_file", "invoice_file"];
/* multi-image column (pipe-separated URLs from backend) */
const MULTI_IMAGE_FIELDS = ["prescriber_photos"];

/* ── small "View" chip that opens the lightbox ── */
const ViewChip = ({ label, url, onView }) => (
  <Tooltip title="Click to enlarge">
    <Chip
      size="small"
      icon={<PhotoCamera sx={{ fontSize: 14 }} />}
      label={label}
      onClick={() => onView(url)}
      clickable
      color="primary"
      variant="outlined"
      sx={{ cursor: "pointer", fontSize: 11 }}
    />
  </Tooltip>
);

const AllSubmissions = () => {
  const [getAdminSubmissions, { isLoading }] = useGetAdminSubmissionsMutation();
  const [tracker,   setTracker]   = useState("rx");
  const [startDate, setStartDate] = useState("");
  const [endDate,   setEndDate]   = useState("");
  const [empNo,     setEmpNo]     = useState("");
  const [rows,      setRows]      = useState([]);
  const [imgPreview, setImgPreview] = useState(null); // full URL to show
  const [imgError,   setImgError]   = useState(false);

  const handleSearch = async () => {
    try {
      const body = { type: tracker };
      if (startDate) body.from = startDate;
      if (endDate)   body.to   = endDate;
      if (empNo)     body.EmpNo = empNo;
      const res = await getAdminSubmissions(body).unwrap();
      setRows(Array.isArray(res) ? res : res.data || []);
    } catch (err) {
      toast.error(err?.data?.message || "Failed to fetch");
    }
  };

  const openImage = (url) => {
    setImgError(false);
    setImgPreview(url);
  };

  const handleExport = () => {
    if (rows.length === 0) { toast.warn("No data to export — run a search first"); return; }
    /* strip image/photo columns from export sheet */
    const IMAGE_SKIP = new Set([...IMAGE_FIELDS, ...MULTI_IMAGE_FIELDS]);
    const exportCols = cols.filter((c) => !IMAGE_SKIP.has(c));
    const exportRows = rows.map((r) =>
      Object.fromEntries(exportCols.map((c) => [c, r[c] ?? ""]))
    );
    const ws = XLSX.utils.json_to_sheet(exportRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, tracker.toUpperCase());
    XLSX.writeFile(wb, `naari_${tracker}_${new Date().toISOString().split("T")[0]}.xlsx`);
    toast.success(`Exported ${rows.length} rows`);
  };

  const columns = {
    rx:      ["EmpNo", "HeadQtr", "DoctName", "DocCategory", "visit_date", "new_rx_count", "folireg_samples_given", "pob_strips", "rx_photo", "pob_invoice", "submitted_at"],
    weekly:  ["EmpNo", "HeadQtr", "week_start_date", "chemist_stock_count", "total_strips_at_chemist", "pob_taken_strips", "prescriber_count", "prescriber_photos", "submitted_at"],
    psp:     ["EmpNo", "HeadQtr", "total_kits_given", "DoctName", "DoctCd", "date_given", "patient_enrolled", "patients_count", "prescription_file", "invoice_file", "submitted_at"],
    lycored: ["EmpNo", "HeadQtr", "DoctName", "practicing_in_ivf", "monthly_potential", "lycored_m_available", "strips_available", "bonanza_given", "prescription_file", "submitted_at"],
  };

  const cols = columns[tracker] || columns.rx;

  const renderCell = (row, col) => {
    const val = row[col];

    /* multi-image: pipe-separated paths from backend */
    if (MULTI_IMAGE_FIELDS.includes(col)) {
      if (!val) return <Typography variant="caption" color="text.secondary">—</Typography>;
      const photos = val.split("||").filter(Boolean);
      return (
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
          {photos.map((path, i) => (
            <ViewChip
              key={i}
              label={`Photo ${i + 1}`}
              url={`${BASE}${path}`}
              onView={openImage}
            />
          ))}
        </Box>
      );
    }

    /* single image field */
    if (IMAGE_FIELDS.includes(col)) {
      if (!val) return <Typography variant="caption" color="text.secondary">—</Typography>;
      const url = val.startsWith("http") ? val : `${BASE}${val}`;
      return <ViewChip label="View Photo" url={url} onView={openImage} />;
    }

    /* boolean */
    if (typeof val === "boolean" || val === 0 || val === 1) {
      if (col === "practicing_in_ivf" || col === "lycored_m_available" || col === "bonanza_given") {
        return (
          <Chip
            size="small"
            label={val ? "Yes" : "No"}
            color={val ? "success" : "default"}
            variant="outlined"
            sx={{ fontSize: 11 }}
          />
        );
      }
    }

    /* date strings */
    const str = String(val ?? "—");
    if (str.includes("T")) return str.split("T")[0];
    return str || "—";
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>All Submissions</Typography>

      {/* ── Filters ── */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3, alignItems: "center" }}>
        <FormControl sx={{ minWidth: 160 }}>
          <InputLabel>Tracker</InputLabel>
          <Select value={tracker} label="Tracker"
            onChange={(e) => { setTracker(e.target.value); setRows([]); }}>
            <MenuItem value="rx">Rx Tracker</MenuItem>
            <MenuItem value="weekly">Weekly Tracker</MenuItem>
            <MenuItem value="psp">PSP Tracker</MenuItem>
            <MenuItem value="lycored">Lycored Tracker</MenuItem>
          </Select>
        </FormControl>

        <TextField type="date" label="Start Date" value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }} />

        <TextField type="date" label="End Date" value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }} />

        <TextField label="EmpNo (optional)" value={empNo}
          onChange={(e) => setEmpNo(e.target.value)} sx={{ width: 180 }} />

        <Button variant="contained" onClick={handleSearch} disabled={isLoading}
          sx={{ height: 56, minWidth: 110 }}>
          {isLoading ? <CircularProgress size={20} color="inherit" /> : "Search"}
        </Button>

        <Button
          variant="outlined"
          color="success"
          onClick={handleExport}
          disabled={rows.length === 0}
          startIcon={<Download />}
          sx={{ height: 56, minWidth: 140 }}
        >
          Export Excel
        </Button>
      </Box>

      {rows.length > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {rows.length} record{rows.length !== 1 ? "s" : ""} found
        </Typography>
      )}

      {/* ── Table ── */}
      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ "& th": { fontWeight: 700, whiteSpace: "nowrap" } }}>
              {cols.map((c) => (
                <TableCell key={c}>{c.replace(/_/g, " ")}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, i) => (
              <TableRow key={i} hover>
                {cols.map((c) => (
                  <TableCell key={c} sx={{ whiteSpace: "nowrap" }}>
                    {renderCell(row, c)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={cols.length} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  Use filters above and click Search
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Image Lightbox Dialog ── */}
      <Dialog
        open={!!imgPreview}
        onClose={() => setImgPreview(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { background: "#000", borderRadius: 2 } }}
      >
        <DialogContent sx={{ p: 0, position: "relative", display: "flex", alignItems: "center", justifyContent: "center", minHeight: 300 }}>
          {/* Close button */}
          <IconButton
            onClick={() => setImgPreview(null)}
            sx={{
              position: "absolute", top: 10, right: 10, zIndex: 10,
              background: "rgba(0,0,0,0.6)", color: "#fff",
              "&:hover": { background: "rgba(0,0,0,0.85)" },
            }}
          >
            <Close />
          </IconButton>

          {/* Open in new tab */}
          <Tooltip title="Open in new tab">
            <IconButton
              component="a"
              href={imgPreview}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                position: "absolute", top: 10, right: 56, zIndex: 10,
                background: "rgba(0,0,0,0.6)", color: "#fff",
                "&:hover": { background: "rgba(0,0,0,0.85)" },
              }}
            >
              <ZoomIn />
            </IconButton>
          </Tooltip>

          {imgError ? (
            <Box sx={{ textAlign: "center", py: 6, color: "#fff" }}>
              <Typography variant="h6" gutterBottom>Image not available</Typography>
              <Typography variant="body2" sx={{ opacity: 0.6 }}>{imgPreview}</Typography>
              <Button
                component="a"
                href={imgPreview}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                sx={{ mt: 2, color: "#fff", borderColor: "#fff" }}
              >
                Try opening directly
              </Button>
            </Box>
          ) : (
            <Box
              component="img"
              src={imgPreview}
              alt="Submission photo"
              onError={() => setImgError(true)}
              sx={{
                maxWidth: "100%",
                maxHeight: "88vh",
                objectFit: "contain",
                display: "block",
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AllSubmissions;
